import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { sendChat } from "../lib/api";

const sessionId = `hgdc-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const suggestions = [
  "What should I bring for my first visit?",
  "Can I browse the menu online?",
  "Do non-DC residents need to register?",
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Welcome to Higher Ground DC. I'm your concierge — ask me about visiting, registration, products, hours, or events." },
  ]);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { reply } = await sendChat({ session_id: sessionId, message: q });
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please call us at (202) 800-1135." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-toggle"
        className="fixed bottom-6 right-6 z-[120] flex h-14 w-14 items-center justify-center rounded-full bg-gold text-ink shadow-lg shadow-black/40 transition-transform duration-300 hover:scale-105"
        aria-label="Open chat"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[120] flex h-[540px] max-h-[calc(100vh-8rem)] w-[calc(100vw-3rem)] max-w-[400px] flex-col border border-white/10 bg-ink-2 shadow-2xl shadow-black/60"
            data-testid="chat-panel"
          >
            <div className="flex items-center gap-3 border-b border-white/10 bg-ink p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/15 text-gold">
                <MessageCircle className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm uppercase tracking-[0.12em] text-white">Higher Ground Concierge</p>
                <p className="text-[11px] text-white/40">Typically replies instantly</p>
              </div>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-gold text-ink" : "border border-white/10 bg-ink text-white/80"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 border border-white/10 bg-ink px-4 py-3 text-sm text-white/50">
                    <Loader2 className="h-4 w-4 animate-spin text-gold" /> Thinking…
                  </div>
                </div>
              )}
              {messages.length === 1 && !loading && (
                <div className="space-y-2 pt-2">
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="chat-suggestion" className="block w-full border border-white/10 px-3 py-2 text-left text-xs text-white/60 transition-colors hover:border-gold/50 hover:text-white">
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-white/10 bg-ink p-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about visiting, products…"
                data-testid="chat-input"
                className="flex-1 bg-transparent px-3 py-2 text-sm text-white placeholder-white/35 outline-none"
              />
              <button type="submit" disabled={loading} data-testid="chat-send" className="flex h-10 w-10 items-center justify-center bg-gold text-ink transition-colors hover:bg-champagne disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
